import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { mapTagsToAcceptableByBackendFormat } from "src/components/Tag/TagsFormInput";
import useFlash from "go-alert/AlertMessage";
import { mapCustomFieldsToForm } from "go-app/components/CustomFields";
import CustomFieldsForm from "go-app/components/CustomFields/CustomFieldsForm";
import handleError from "go-app/services/errors";
import { formatDateTimeToString } from "go-core/components/Formatters/FormatDate";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { selectOrganization } from "go-security/services/organizations/selectors";
import AttachmentsForm from "../../../../../../../../../components/Attachment/AttachmentsForm";
import { StocktakingApi } from "../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../services/Api/api";
import { StocktakingContext } from "../../Preview";
import StocktakingBasicInfoForm from "./StocktakingBasicInfoForm";

const StocktakingForm = () => {
	const { stocktaking, customFieldsConfig } = useContext(StocktakingContext);
	const form = useForm<StocktakingApi>({
		criteriaMode: "all",
		defaultValues: {
			...stocktaking,
			stocktaking_at: stocktaking?.stocktaking_at ?? formatDateTimeToString(new Date()),
			type: stocktaking?.type ?? "AVAILABILITY",
			custom_fields: mapCustomFieldsToForm(customFieldsConfig || [], stocktaking?.custom_fields || []),
		},
	});
	const { t } = useTranslation();
	const { formState, handleSubmit, setError, reset } = form;
	const { addFlash, addSuccessFlash } = useFlash();
	const [loading, setLoading] = useState<boolean>(false);
	const params = { include: "stock,tags,custom_fields,attachments" };
	const history = useHistory();
	const organization = useSelector(selectOrganization);
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);

	const onSubmit = handleSubmit(async (data: StocktakingApi) => {
		if (!validateCustomErrors()) {
			return;
		}

		setLoading(true);
		try {
			let res;
			if (data.tags) data.tags = mapTagsToAcceptableByBackendFormat(data.tags);
			if (stocktaking?.id) {
				res = await api.organization().updateStocktaking(data, params);
				reset({
					...res,
					custom_fields: mapCustomFieldsToForm(customFieldsConfig || [], res?.custom_fields || []),
				});
			} else {
				res = await api.organization().createStocktaking(data);
				history.push(`/${organization.id}/stocktakings/${res.id}/edit`);
			}
			addSuccessFlash(t("lib:common.flash.saved"));
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	return (
		<FormDirty formState={formState} loading={loading} noValidate key="stocktaking-form" onSubmit={onSubmit}>
			<h5>{t("common.word.basic_info")}</h5>
			<StocktakingBasicInfoForm stocktaking={stocktaking} form={form} setErrors={setErrors} />

			{customFieldsConfig && customFieldsConfig.length > 0 && (
				<fieldset className="form-group">
					<h5>{t("common.word.custom_fields")}</h5>
					<CustomFieldsForm
						customFieldsConfig={customFieldsConfig}
						customFields={stocktaking?.custom_fields || []}
						form={form}
						setErrors={setErrors}
					/>
				</fieldset>
			)}

			<span className="mt-4 d-block">{t("common.word.attachments")}</span>
			<AttachmentsForm form={form} />
		</FormDirty>
	);
};

export default StocktakingForm;
